import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
import { validatePassword } from '../utils/passwordValidator';

// ----------------------- Inline style helpers -----------------------
const PALETTE = {
  primary: '#2563eb',
  primaryDark: '#1d4ed8',
  accent: '#1e3a5f',
  success: '#10b981',
  danger: '#ef4444',
  warning: '#f59e0b',
  textDark: '#111827',
  textMuted: '#6b7280',
  border: '#e5e7eb',
  borderFocus: '#2563eb',
  bgSoft: '#f9fafb',
};

const getStrengthColor = (score) => {
  if (score < 30) return '#ef4444';
  if (score < 50) return '#f97316';
  if (score < 70) return '#facc15';
  if (score < 85) return '#22c55e';
  return '#10b981';
};

// ----------------------- Small reusable atoms -----------------------
const InteractiveIcon = ({ children, onClick, label, active }) => (
  <button
    type="button"
    aria-label={label}
    onClick={(e) => {
      if (typeof onClick === 'function') onClick(e);
    }}
    style={{
      position: 'absolute',
      right: '0.6rem',
      top: '50%',
      transform: 'translateY(-50%)',
      background: active ? 'rgba(102, 126, 234, 0.12)' : 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: active ? PALETTE.primary : PALETTE.textMuted,
      fontSize: '1rem',
      width: 30,
      height: 30,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background 0.2s ease, color 0.2s ease, transform 0.2s ease',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
  >
    {children}
  </button>
);

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.77 19.77 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.77 19.77 0 0 1-3.17 4.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const CheckIcon = ({ color = PALETTE.success, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = ({ color = PALETTE.textMuted, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const LockIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ----------------------- Interactive password input -----------------------
const PasswordField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  disabled,
  show,
  onToggleShow,
  autoFocus,
  trailing,
  hint,
  hintColor,
}) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '0.4rem',
          fontWeight: 600,
          fontSize: '0.85rem',
          color: focused ? PALETTE.primary : PALETTE.textDark,
          transition: 'color 0.2s ease',
          letterSpacing: '0.01em',
        }}
      >
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: '100%',
            padding: '0.8rem 5rem 0.8rem 0.9rem',
            border: `1.5px solid ${focused ? PALETTE.borderFocus : PALETTE.border}`,
            borderRadius: '10px',
            fontSize: '0.95rem',
            boxSizing: 'border-box',
            outline: 'none',
            background: disabled ? '#f3f4f6' : 'white',
            boxShadow: focused ? '0 0 0 4px rgba(102, 126, 234, 0.15)' : 'none',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            color: PALETTE.textDark,
          }}
        />
        <div style={{ position: 'absolute', right: trailing ? '2.4rem' : 0, top: 0, height: '100%' }}>
          <InteractiveIcon
            onClick={onToggleShow}
            label={show ? 'Hide password' : 'Show password'}
            active={show}
          >
            <EyeIcon open={show} />
          </InteractiveIcon>
        </div>
        {trailing && (
          <div
            style={{
              position: 'absolute',
              right: '0.7rem',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {trailing}
          </div>
        )}
      </div>
      {hint && (
        <div
          style={{
            marginTop: '0.35rem',
            fontSize: '0.78rem',
            color: hintColor || PALETTE.textMuted,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            animation: 'cp-fade-in 0.25s ease',
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
};

// ----------------------- Strength meter -----------------------
const StrengthMeter = ({ password, score, strength }) => {
  if (!password) return null;
  const color = getStrengthColor(score);
  return (
    <div style={{ marginTop: '0.6rem' }}>
      <div
        style={{
          height: 6,
          backgroundColor: '#eef0f3',
          borderRadius: 6,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${score}%`,
            background: `linear-gradient(90deg, ${color}, ${color}cc)`,
            transition: 'width 0.45s cubic-bezier(0.16, 1, 0.3, 1), background 0.3s ease',
            boxShadow: `0 0 8px ${color}66`,
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 6,
          fontSize: '0.78rem',
        }}
      >
        <span style={{ color, fontWeight: 600, letterSpacing: '0.02em' }}>
          {strength}
        </span>
        <span style={{ color: PALETTE.textMuted, fontVariantNumeric: 'tabular-nums' }}>
          {score}/100
        </span>
      </div>
    </div>
  );
};

// ----------------------- Live requirements checklist -----------------------
const RequirementRow = ({ met, children }) => (
  <li
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '4px 0',
      color: met ? PALETTE.success : PALETTE.textMuted,
      fontSize: '0.83rem',
      fontWeight: met ? 600 : 500,
      transition: 'color 0.25s ease',
    }}
  >
    <span
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: met ? 'rgba(16, 185, 129, 0.12)' : 'rgba(107, 114, 128, 0.08)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.25s ease, transform 0.25s ease',
        transform: met ? 'scale(1.05)' : 'scale(1)',
      }}
    >
      {met ? <CheckIcon /> : <XIcon />}
    </span>
    <span style={{ textDecoration: met ? 'line-through' : 'none', opacity: met ? 0.85 : 1 }}>
      {children}
    </span>
  </li>
);

const computeRules = (password) => ({
  length: password.length >= 8,
  upper: /[A-Z]/.test(password),
  lower: /[a-z]/.test(password),
  number: /[0-9]/.test(password),
  special: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password),
  noSpace: password.length > 0 && !/\s/.test(password),
});

// ----------------------- Page -----------------------
const ChangePasswordPage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [btnHover, setBtnHover] = useState(false);
  const [btnPressed, setBtnPressed] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');

  React.useEffect(() => {
    if (!user.username || !token) {
      navigate('/login');
    }
  }, [user, token, navigate]);

  const validation = useMemo(
    () => (formData.newPassword ? validatePassword(formData.newPassword) : null),
    [formData.newPassword]
  );
  const rules = useMemo(() => computeRules(formData.newPassword), [formData.newPassword]);
  const allRulesMet = Object.values(rules).every(Boolean);
  const passwordsMatch =
    formData.confirmPassword.length > 0 &&
    formData.newPassword === formData.confirmPassword;
  const formReady =
    !!formData.currentPassword &&
    allRulesMet &&
    passwordsMatch &&
    formData.currentPassword !== formData.newPassword;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (message || error) {
      setMessage('');
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (!allRulesMet) {
      setError('New password does not meet security requirements');
      return;
    }
    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('Password changed successfully!');
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => setMessage(''), 5000);
      } else {
        setError(
          data.details
            ? `${data.error}: ${data.details.join(', ')}`
            : data.error || 'An error occurred while changing your password'
        );
      }
    } catch (err) {
      console.error('Change password error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const matchHint = formData.confirmPassword
    ? passwordsMatch
      ? {
          node: (
            <>
              <CheckIcon /> Passwords match
            </>
          ),
          color: PALETTE.success,
        }
      : {
          node: (
            <>
              <XIcon color={PALETTE.danger} /> Passwords do not match
            </>
          ),
          color: PALETTE.danger,
        }
    : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 50%, #fdf2f8 100%)',
        padding: '2.5rem 1rem',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      <style>{`
        @keyframes cp-card-in {
          from { opacity: 0; transform: translateY(18px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes cp-fade-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes cp-shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-6px); }
          40%, 80% { transform: translateX(6px); }
        }
        @keyframes cp-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div
        style={{
          width: '100%',
          maxWidth: 560,
          backgroundColor: 'white',
          borderRadius: 18,
          boxShadow: '0 20px 60px rgba(102, 126, 234, 0.18), 0 4px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          animation: 'cp-card-in 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Gradient header */}
        <div
          style={{
            background: `linear-gradient(135deg, ${PALETTE.primary} 0%, ${PALETTE.accent} 100%)`,
            padding: '1.6rem 2rem',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: 'rgba(255, 255, 255, 0.18)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          >
            <LockIcon />
          </div>
          <div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.01em' }}>
              Change Password
            </div>
            <div style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: 2 }}>
              Signed in as <strong>{user.username}</strong>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '1.6rem 2rem 2rem 2rem' }}>
          {message && (
            <div
              role="status"
              style={{
                backgroundColor: '#ecfdf5',
                border: '1px solid #a7f3d0',
                color: '#065f46',
                padding: '0.75rem 0.9rem',
                borderRadius: 10,
                marginBottom: '1rem',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                animation: 'cp-fade-in 0.3s ease',
              }}
            >
              <CheckIcon color="#065f46" size={16} />
              {message}
            </div>
          )}

          {error && (
            <div
              role="alert"
              style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                padding: '0.75rem 0.9rem',
                borderRadius: 10,
                marginBottom: '1rem',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                animation: 'cp-shake 0.4s ease',
              }}
            >
              <XIcon color="#991b1b" size={16} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <PasswordField
              label="Current Password"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleInputChange}
              placeholder="Enter your current password"
              disabled={loading}
              show={show.current}
              onToggleShow={() => setShow((s) => ({ ...s, current: !s.current }))}
              autoFocus
            />

            <PasswordField
              label="New Password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              placeholder="Create a strong new password"
              disabled={loading}
              show={show.next}
              onToggleShow={() => setShow((s) => ({ ...s, next: !s.next }))}
            />
            {validation && (
              <StrengthMeter
                password={formData.newPassword}
                score={validation.score}
                strength={validation.strength}
              />
            )}

            {/* Live requirements card */}
            <div
              style={{
                marginTop: '0.9rem',
                marginBottom: '1.1rem',
                padding: '0.85rem 1rem',
                background: PALETTE.bgSoft,
                border: `1px solid ${PALETTE.border}`,
                borderRadius: 12,
              }}
            >
              <div
                style={{
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: PALETTE.textMuted,
                  marginBottom: 6,
                }}
              >
                Password requirements
              </div>
              <ul
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  columnGap: 16,
                }}
              >
                <RequirementRow met={rules.length}>At least 8 characters</RequirementRow>
                <RequirementRow met={rules.upper}>One uppercase letter</RequirementRow>
                <RequirementRow met={rules.lower}>One lowercase letter</RequirementRow>
                <RequirementRow met={rules.number}>One number</RequirementRow>
                <RequirementRow met={rules.special}>One special character</RequirementRow>
                <RequirementRow met={rules.noSpace}>No spaces</RequirementRow>
              </ul>
            </div>

            <PasswordField
              label="Confirm New Password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Re-enter your new password"
              disabled={loading}
              show={show.confirm}
              onToggleShow={() => setShow((s) => ({ ...s, confirm: !s.confirm }))}
              hint={matchHint?.node}
              hintColor={matchHint?.color}
            />

            <button
              type="submit"
              disabled={loading || !formReady}
              onMouseEnter={() => setBtnHover(true)}
              onMouseLeave={() => { setBtnHover(false); setBtnPressed(false); }}
              onMouseDown={() => setBtnPressed(true)}
              onMouseUp={() => setBtnPressed(false)}
              style={{
                width: '100%',
                padding: '0.9rem',
                marginTop: '0.4rem',
                background:
                  loading || !formReady
                    ? '#d1d5db'
                    : `linear-gradient(135deg, ${PALETTE.primary} 0%, ${PALETTE.accent} 100%)`,
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '0.01em',
                cursor: loading || !formReady ? 'not-allowed' : 'pointer',
                transition: 'transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease',
                transform:
                  loading || !formReady
                    ? 'none'
                    : btnPressed
                    ? 'translateY(1px) scale(0.99)'
                    : btnHover
                    ? 'translateY(-1px)'
                    : 'translateY(0)',
                boxShadow:
                  loading || !formReady
                    ? 'none'
                    : btnHover
                    ? '0 10px 24px rgba(102, 126, 234, 0.35)'
                    : '0 6px 14px rgba(102, 126, 234, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              {loading ? (
                <>
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: 'white',
                      display: 'inline-block',
                      animation: 'cp-spin 0.8s linear infinite',
                    }}
                  />
                  Updating password...
                </>
              ) : (
                'Change Password'
              )}
            </button>

            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => navigate(-1)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: PALETTE.primary,
                  cursor: 'pointer',
                  fontSize: '0.88rem',
                  fontWeight: 500,
                  padding: '6px 10px',
                  borderRadius: 6,
                  transition: 'background 0.15s ease, transform 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(102, 126, 234, 0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              >
                ← Go Back
              </button>
            </div>
          </form>

          {/* Security Tips */}
          <div
            style={{
              marginTop: '1.5rem',
              padding: '1rem 1.1rem',
              background: 'linear-gradient(135deg, #f5f3ff 0%, #eef2ff 100%)',
              border: '1px solid #e0e7ff',
              borderRadius: 12,
              fontSize: '0.83rem',
              color: '#4338ca',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6, letterSpacing: '0.02em' }}>
              Security Tips
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.1rem', color: '#4f46e5', lineHeight: 1.6 }}>
              <li>Use a unique password you don't use elsewhere</li>
              <li>Consider a reputable password manager</li>
              <li>Rotate passwords regularly</li>
              <li>Never share your password with anyone</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
